import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import localforage from 'localforage';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import DashboardFooter from '../layouts/dashboard_footer';
import { useAppName, useBaseUrl } from '../../utils/utils';
import './ModernDashboard.css';

type DashboardKey = 'general-service-provider' | 'clinician' | 'facility';

interface DashboardSwitchItem {
    key: DashboardKey;
    name: string;
    route: string;
    endpoint: string;
    is_default: boolean;
}

interface PrimaryStat {
    metric_key: string;
    label: string;
    value: number;
    suffix?: string;
    sub: string;
}

interface CollabItem {
    id: number;
    title: string;
    status: string;
    created: string | null;
}

interface StaffRosterItem {
    id: number;
    fname: string;
    lname: string;
    email: string;
    title: string;
    avatar: string;
    active: boolean;
}

interface LoginItem {
    user_type: string;
    user_name: string;
    created: string | null;
    success: boolean;
}

interface PatientItem {
    patient_id: string;
    first_name: string;
    last_name: string;
    enrolled: string | null;
}

interface FacilityNetworkItem {
    id: number;
    name: string;
    town: string;
    category: string;
    staff_count: number;
    patient_count: number;
    capacity: number;
    utilization: number;
    active: boolean;
}

interface ScheduleItem {
    id: number;
    patient_name: string;
    patient_id: string;
    time: string;
    datetime: string;
    is_past?: boolean;
    date?: string;
}

interface RecordItem {
    id: string;
    record_identifier: string;
    name: string;
    patient_name: string;
    patient_id: string;
    examiner: string;
    chief_complaint: string;
    diagnosis: string;
    status: string;
    requires_follow_up: boolean;
    date_created: string | null;
}

interface PrescriptionItem {
    id: number;
    patient_name: string;
    patient_id: string;
    medication: string;
    dosage: string;
}

interface ReferralItem {
    id: number;
    referral_id: string;
    patient_name: string;
    receiving_facility: string;
    referral_type: string;
    priority: string;
    status: string;
    referral_date: string | null;
    direction: 'inbound' | 'outbound';
}

interface StaffTitleBreakdown {
    title: string;
    count: number;
}

interface ServiceLineItem {
    id: number;
    name: string;
    clinic_name: string;
    minimum_rate: number;
    hourly_rate: number;
    is_billable: boolean;
}

interface BillingOverview {
    clinic_billing_total: number;
    institution_billing_total: number;
    clinic_invoice_total: number;
    institution_invoice_total: number;
    clinic_billing_count: number;
    institution_billing_count: number;
    clinic_invoice_count: number;
    institution_invoice_count: number;
}

interface PatientFlow {
    total_patients: number;
    enrolled_last_30d: number;
    enrolled_last_7d: number;
    appointments_today: number;
    appointments_this_week: number;
}

interface ProviderDashboardData {
    dashboard: {
        key: DashboardKey;
        name: string;
        route: string;
        endpoint: string;
        description: string;
        user_groups: string[];
        default_fallback: boolean;
    };
    available_dashboards: DashboardSwitchItem[];
    access: {
        mode: string;
        allowed_dashboard_keys: DashboardKey[];
    };
    staff: {
        id: number;
        fname: string;
        lname: string;
        email: string;
        title: string;
        categories: string[];
        avatar: string;
    };
    clinic: {
        id: number;
        name: string;
        town: string;
        category: string;
        active: boolean;
        capacity: number;
    };
    institution: {
        id: number;
        name: string;
    };
    metrics: Record<string, number>;
    primary_stats: PrimaryStat[];
    // General
    recent_collaborations?: CollabItem[];
    staff_roster?: StaffRosterItem[];
    recent_logins?: LoginItem[];
    recent_patients?: PatientItem[];
    facility_network?: FacilityNetworkItem[];
    // Clinician
    today_schedule?: ScheduleItem[];
    upcoming_appointments?: ScheduleItem[];
    recent_records?: RecordItem[];
    recent_prescriptions?: PrescriptionItem[];
    active_referrals?: ReferralItem[];
    // Facility
    staff_by_title?: StaffTitleBreakdown[];
    service_lines?: ServiceLineItem[];
    billing_overview?: BillingOverview;
    patient_flow?: PatientFlow;
}

const COLORS = ['#405189', '#0ab39c', '#f7b84b', '#f06548', '#299cdb', '#6c757d'];

const endpointByDashboard: Record<DashboardKey, string> = {
    'general-service-provider': 'core/api/dashboard/general-service-provider/',
    clinician: 'core/api/dashboard/clinician/',
    facility: 'core/api/dashboard/facility/',
};

const heroByDashboard: Record<DashboardKey, { icon: string; actionLabel: string; actionLink: string }> = {
    'general-service-provider': {
        icon: 'bx bx-briefcase-alt-2',
        actionLabel: 'Open Service Center',
        actionLink: '/institution/provider/service-center',
    },
    clinician: {
        icon: 'bx bx-plus-medical',
        actionLabel: 'Open EHR',
        actionLink: '/institution/clinician/ehr',
    },
    facility: {
        icon: 'bx bx-buildings',
        actionLabel: 'Manage Facility',
        actionLink: '/institution/facility/management',
    },
};

const quickAccessByDashboard: Record<DashboardKey, Array<{ to: string; icon: string; label: string; sub: string; color: string; bg: string }>> = {
    'general-service-provider': [
        { to: '/institution/provider/service-center', icon: 'bx bx-layer', label: 'Service Center', sub: 'Requests and offerings', color: '#405189', bg: 'rgba(64,81,137,.12)' },
        { to: '/institution/provider/collaborations', icon: 'bx bx-link-alt', label: 'Collaborations', sub: 'Shared service delivery', color: '#0ab39c', bg: 'rgba(10,179,156,.12)' },
        { to: '/institution/provider/resources', icon: 'bx bx-book-open', label: 'Resources', sub: 'Tools and materials', color: '#299cdb', bg: 'rgba(41,156,219,.12)' },
        { to: '/institution/provider/communications', icon: 'bx bx-message-rounded-dots', label: 'Communications', sub: 'Cross-team communication', color: '#f7b84b', bg: 'rgba(247,184,75,.12)' },
        { to: '/institution/provider/telehealth', icon: 'bx bx-video', label: 'Telehealth', sub: 'Consult and coordinate', color: '#f06548', bg: 'rgba(240,101,72,.12)' },
        { to: '/institution/provider/subscription', icon: 'bx bx-credit-card', label: 'My Subscription', sub: 'Billing and access', color: '#6c757d', bg: 'rgba(108,117,125,.12)' },
        { to: '/admin/dashboards', icon: 'bx bx-line-chart', label: 'Analytics', sub: 'Platform-wide dashboards', color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
    ],
    clinician: [
        { to: '/institution/clinician/ehr', icon: 'bx bx-notepad', label: 'Clinical Records', sub: 'Review patient charts', color: '#405189', bg: 'rgba(64,81,137,.12)' },
        { to: '/institution/clinician/ehr/new', icon: 'bx bx-plus-medical', label: 'New EHR', sub: 'Document patient care', color: '#0ab39c', bg: 'rgba(10,179,156,.12)' },
        { to: '/institution/clinician/schedule', icon: 'bx bx-calendar', label: 'Schedule', sub: 'Appointments and rounds', color: '#299cdb', bg: 'rgba(41,156,219,.12)' },
        { to: '/institution/clinician/patients', icon: 'bx bx-user-circle', label: 'Patients', sub: 'Patient roster', color: '#f7b84b', bg: 'rgba(247,184,75,.12)' },
        { to: '/institution/clinician/referrals', icon: 'bx bx-transfer-alt', label: 'Referrals', sub: 'Coordinate care', color: '#f06548', bg: 'rgba(240,101,72,.12)' },
        { to: '/institution/clinician/subscription', icon: 'bx bx-credit-card', label: 'My Subscription', sub: 'Billing and access', color: '#6c757d', bg: 'rgba(108,117,125,.12)' },
        { to: '/admin/dashboards', icon: 'bx bx-line-chart', label: 'Analytics', sub: 'Platform-wide dashboards', color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
    ],
    facility: [
        { to: '/institution/facility/facilities', icon: 'bx bx-buildings', label: 'Facilities', sub: 'Network sites', color: '#405189', bg: 'rgba(64,81,137,.12)' },
        { to: '/institution/facility/staff', icon: 'bx bx-group', label: 'Workforce', sub: 'Staff management', color: '#0ab39c', bg: 'rgba(10,179,156,.12)' },
        { to: '/institution/facility/patients', icon: 'bx bx-user-pin', label: 'Patients', sub: 'Facility patient flow', color: '#299cdb', bg: 'rgba(41,156,219,.12)' },
        { to: '/institution/facility/services', icon: 'bx bx-briefcase-alt', label: 'Services', sub: 'Care delivery lines', color: '#f7b84b', bg: 'rgba(247,184,75,.12)' },
        { to: '/institution/facility/accounting', icon: 'bx bx-receipt', label: 'Finance', sub: 'Invoices and billing', color: '#f06548', bg: 'rgba(240,101,72,.12)' },
        { to: '/institution/facility/subscription', icon: 'bx bx-credit-card', label: 'My Subscription', sub: 'Billing and access', color: '#6c757d', bg: 'rgba(108,117,125,.12)' },
        { to: '/admin/dashboards', icon: 'bx bx-line-chart', label: 'Analytics', sub: 'Platform-wide dashboards', color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
    ],
};

function getInitials(fname: string, lname: string): string {
    return `${(fname || '')[0] || ''}${(lname || '')[0] || ''}`.toUpperCase();
}

function randomColor(seed: string): string {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    return COLORS[Math.abs(hash) % COLORS.length];
}

function greeting(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(iso: string | null): string {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

const statusBadge = (s: string) => {
    const map: Record<string, string> = { active: 'uz-badge-active', pending: 'uz-badge-pending', completed: 'uz-badge-completed', closed: 'uz-badge-closed' };
    return map[s?.toLowerCase()] || 'uz-badge-pending';
};

const SkeletonDashboard: React.FC = () => (
    <div className="container-fluid">
        <div className="uz-skeleton mb-4" style={{ height: 140 }} />
        <div className="row g-3 mb-4">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="col"><div className="uz-skeleton" style={{ height: 100 }} /></div>)}
        </div>
        <div className="row g-3 mb-4">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="col-md-4 col-lg-2"><div className="uz-skeleton" style={{ height: 120 }} /></div>)}
        </div>
        <div className="row g-4">
            <div className="col-lg-8"><div className="uz-skeleton" style={{ height: 340 }} /></div>
            <div className="col-lg-4"><div className="uz-skeleton" style={{ height: 340 }} /></div>
        </div>
    </div>
);

interface ProviderDashboardProps {
    dashboardKey: DashboardKey;
}

const ProviderDashboard: React.FC<ProviderDashboardProps> = ({ dashboardKey }) => {
    const location = useLocation();
    const baseUrl = useBaseUrl();
    const appName = useAppName();
    const [data, setData] = useState<ProviderDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [localName, setLocalName] = useState({ fname: '', lname: '', clinic: '' });

    useEffect(() => {
        document.title = appName;
        const rootEl = document.getElementById('root');
        if (rootEl) rootEl.setAttribute('data-layout', 'semi-box');

        Promise.all([
            localforage.getItem<string>('fname'),
            localforage.getItem<string>('lname'),
            localforage.getItem<string>('clinic_name'),
        ]).then(([f, l, c]) => setLocalName({ fname: f || '', lname: l || '', clinic: c || '' }));
    }, [appName]);

    const fetchDashboard = useCallback(async (background = false) => {
        if (background) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError('');
        try {
            const resp = await axios.get(`${baseUrl}${endpointByDashboard[dashboardKey]}`, {
                withCredentials: true,
                timeout: 15000,
            });
            if (resp.data?.status === 'success') {
                setData(resp.data.data);
            } else {
                setError(resp.data?.message || 'Failed to load dashboard');
            }
        } catch (err: any) {
            console.error('Provider dashboard fetch error:', err);
            setError(err?.response?.data?.message || 'Unable to load dashboard data right now.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [baseUrl, dashboardKey]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard, location.pathname]);

    const hero = heroByDashboard[dashboardKey];
    const quickAccess = quickAccessByDashboard[dashboardKey];
    const staffName = data?.staff ? `${data.staff.fname} ${data.staff.lname}` : `${localName.fname} ${localName.lname}`.trim();
    const clinicName = data?.clinic?.name || localName.clinic || 'Your Facility';
    const todayStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dashboardName = data?.dashboard?.name || 'Dashboard';
    const userGroups = data?.dashboard?.user_groups || [];
    const availableDashboards = data?.available_dashboards || [];

    return (
        <div id="layout-wrapper">
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            <div className="main-content">
                <div className="page-content uz-dash-page" style={{ background: '#f8fcfb' }}>
                    {loading ? (
                        <SkeletonDashboard />
                    ) : (
                        <div className="container-fluid">
                            {error && (
                                <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                                    <i className="bx bx-error-circle fs-5 me-2" />
                                    <div>{error}</div>
                                    <button className="btn btn-sm btn-outline-warning ms-auto" onClick={() => fetchDashboard()}>
                                        Retry
                                    </button>
                                </div>
                            )}

                            <div className="uz-dash-banner mb-4">
                                <div className="row align-items-center g-3">
                                    <div className="col-lg-7">
                                        <div className="uz-date mb-1">{todayStr}</div>
                                        <h2>{greeting()}, {staffName || 'there'}!</h2>
                                        <p>
                                            <strong>{dashboardName}</strong> for <strong>{clinicName}</strong>.
                                            {' '}{data?.dashboard?.description}
                                        </p>
                                    </div>
                                    <div className="col-lg-5">
                                        <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                                            <Link to={hero.actionLink} className="btn btn-light btn-sm rounded-pill px-3">
                                                <i className={`${hero.icon} me-1`} /> {hero.actionLabel}
                                            </Link>
                                            <button className="btn btn-outline-light btn-sm rounded-pill px-3" onClick={() => fetchDashboard(true)}>
                                                <i className={`bx ${refreshing ? 'bx-loader-alt bx-spin' : 'bx-refresh'} me-1`} /> Refresh
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4 mb-4">
                                <div className="col-lg-8">
                                    <div className="uz-panel">
                                        <div className="uz-panel-header">
                                            <h6 className="uz-section-title mb-0"><i className="bx bx-layout text-primary" /> Dashboard Access</h6>
                                        </div>
                                        <div className="uz-panel-body">
                                            <div className="row g-3">
                                                {availableDashboards.map(item => (
                                                    <div key={item.key} className="col-md-4">
                                                        <Link
                                                            to={item.route}
                                                            className="uz-quick-card"
                                                            style={{
                                                                border: item.key === dashboardKey ? '1px solid rgba(10,179,156,.35)' : '1px solid #eef2f7',
                                                                background: item.key === dashboardKey ? '#f2fbf8' : '#fff',
                                                            }}
                                                        >
                                                            <div className="uz-quick-icon" style={{ background: item.key === dashboardKey ? 'rgba(10,179,156,.14)' : 'rgba(64,81,137,.08)', color: item.key === dashboardKey ? '#0ab39c' : '#405189' }}>
                                                                <i className={item.key === 'facility' ? 'bx bx-buildings' : item.key === 'clinician' ? 'bx bx-plus-medical' : 'bx bx-briefcase-alt-2'} />
                                                            </div>
                                                            <h6>{item.name}</h6>
                                                            <small>{item.is_default ? 'Default fallback dashboard' : 'Available to all current users'}</small>
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className="uz-panel h-100">
                                        <div className="uz-panel-header">
                                            <h6 className="uz-section-title mb-0"><i className="bx bx-user-check text-success" /> User Groups</h6>
                                        </div>
                                        <div className="uz-panel-body">
                                            <div className="text-muted small mb-2">All existing authenticated staff users currently have full access.</div>
                                            {userGroups.map(group => (
                                                <div key={group} className="d-flex align-items-start gap-2 mb-2">
                                                    <i className="bx bx-check-circle text-success fs-5" />
                                                    <div style={{ fontSize: '.84rem' }}>{group}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3 mb-4">
                                {(data?.primary_stats || []).map(stat => (
                                    <StatCard
                                        key={stat.metric_key}
                                        icon={iconForMetric(stat.metric_key)}
                                        bg={bgForMetric(stat.metric_key)}
                                        color={colorForMetric(stat.metric_key)}
                                        value={`${Number(stat.value).toLocaleString()}${stat.suffix || ''}`}
                                        label={stat.label}
                                        sub={stat.sub}
                                    />
                                ))}
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="uz-section-title"><i className="bx bx-grid-alt text-primary" /> Quick Access</h5>
                            </div>
                            <div className="row g-3 mb-4">
                                {quickAccess.map(q => (
                                    <div key={q.to} className="col-6 col-md-4 col-lg-2">
                                        <Link to={q.to} className="uz-quick-card">
                                            <div className="uz-quick-icon" style={{ background: q.bg, color: q.color }}>
                                                <i className={q.icon} />
                                            </div>
                                            <h6>{q.label}</h6>
                                            <small>{q.sub}</small>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {dashboardKey === 'clinician' && (
                                <ClinicianPanels data={data} />
                            )}
                            {dashboardKey === 'facility' && (
                                <FacilityPanels data={data} />
                            )}
                            {dashboardKey === 'general-service-provider' && (
                                <GeneralPanels data={data} />
                            )}
                        </div>
                    )}
                </div>
                <DashboardFooter />
            </div>
        </div>
    );
};

function iconForMetric(metricKey: string) {
    const map: Record<string, string> = {
        clinic_patients: 'bx bx-user-circle',
        clinic_staff: 'bx bx-group',
        clinic_services: 'bx bx-layer',
        active_collaborations: 'bx bx-link-alt',
        institution_clinics: 'bx bx-buildings',
        today_appointments: 'bx bx-calendar-check',
        upcoming_appointments: 'bx bx-calendar-event',
        clinic_records: 'bx bx-file-blank',
        clinic_prescriptions: 'bx bx-capsule',
        institution_staff: 'bx bx-user-pin',
        institution_patients: 'bx bx-user',
        institution_services: 'bx bx-briefcase-alt',
        capacity_utilization: 'bx bx-pie-chart-alt-2',
    };
    return map[metricKey] || 'bx bx-bar-chart';
}

function colorForMetric(metricKey: string) {
    const map: Record<string, string> = {
        clinic_patients: '#405189',
        clinic_staff: '#0ab39c',
        clinic_services: '#299cdb',
        active_collaborations: '#f7b84b',
        institution_clinics: '#f06548',
        today_appointments: '#405189',
        upcoming_appointments: '#0ab39c',
        clinic_records: '#299cdb',
        clinic_prescriptions: '#f7b84b',
        institution_staff: '#0ab39c',
        institution_patients: '#299cdb',
        institution_services: '#f7b84b',
        capacity_utilization: '#f06548',
    };
    return map[metricKey] || '#405189';
}

function bgForMetric(metricKey: string) {
    const color = colorForMetric(metricKey);
    if (color === '#0ab39c') return 'rgba(10,179,156,.12)';
    if (color === '#299cdb') return 'rgba(41,156,219,.12)';
    if (color === '#f7b84b') return 'rgba(247,184,75,.12)';
    if (color === '#f06548') return 'rgba(240,101,72,.12)';
    return 'rgba(64,81,137,.12)';
}

interface StatCardProps {
    icon: string;
    bg: string;
    color: string;
    value: string;
    label: string;
    sub: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, bg, color, value, label, sub }) => (
    <div className="col">
        <div className="uz-stat-card">
            <div className="d-flex align-items-start gap-3">
                <div className="uz-stat-icon" style={{ background: bg, color }}>
                    <i className={icon} />
                </div>
                <div>
                    <div className="uz-stat-value">{value}</div>
                    <div className="uz-stat-label">{label}</div>
                    <div className="uz-stat-sub">{sub}</div>
                </div>
            </div>
        </div>
    </div>
);

/* ─── Clinician Panels ──────────────────────────────────────────────── */
const ClinicianPanels: React.FC<{ data: ProviderDashboardData | null }> = ({ data }) => {
    const schedule = data?.today_schedule || [];
    const upcoming = data?.upcoming_appointments || [];
    const records = data?.recent_records || [];
    const prescriptions = data?.recent_prescriptions || [];
    const referrals = data?.active_referrals || [];
    const roster = data?.staff_roster || [];

    return (
        <>
            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-calendar-check text-primary" /> Today's Schedule</h6>
                            <Link to="/institution/clinician/schedule" className="btn btn-sm btn-outline-primary rounded-pill">Full Schedule</Link>
                        </div>
                        <div className="uz-panel-body">
                            {schedule.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-calendar-check fs-1 d-block mb-2 opacity-50" />
                                    No appointments scheduled for today.
                                </div>
                            ) : (
                                schedule.map(appt => (
                                    <div key={appt.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 40, height: 40, fontSize: '1rem', background: appt.is_past ? 'rgba(108,117,125,.10)' : 'rgba(64,81,137,.10)', color: appt.is_past ? '#6c757d' : '#405189' }}>
                                            <i className={appt.is_past ? 'bx bx-check' : 'bx bx-time'} />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.86rem' }}>{appt.patient_name}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>ID: {appt.patient_id}</div>
                                        </div>
                                        <div className="text-end">
                                            <span className="fw-semibold" style={{ fontSize: '.88rem', color: appt.is_past ? '#878a99' : '#405189' }}>{appt.time}</span>
                                            {appt.is_past && <div style={{ fontSize: '.68rem', color: '#878a99' }}>Completed</div>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-calendar-event text-success" /> Upcoming (7 days)</h6>
                        </div>
                        <div className="uz-panel-body">
                            {upcoming.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-calendar-event fs-1 d-block mb-2 opacity-50" />
                                    No upcoming appointments this week.
                                </div>
                            ) : (
                                upcoming.map(appt => (
                                    <div key={appt.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 40, height: 40, fontSize: '1rem', background: 'rgba(10,179,156,.10)', color: '#0ab39c' }}>
                                            <i className="bx bx-calendar" />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.86rem' }}>{appt.patient_name}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>ID: {appt.patient_id}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-semibold" style={{ fontSize: '.82rem' }}>{appt.date}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{appt.time}</div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-file-blank text-primary" /> Recent Clinical Records</h6>
                            <Link to="/institution/clinician/ehr" className="btn btn-sm btn-outline-primary rounded-pill">View All</Link>
                        </div>
                        <div className="uz-panel-body">
                            {records.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-file-blank fs-1 d-block mb-2 opacity-50" />
                                    No clinical records yet.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '.82rem' }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Record</th>
                                                <th>Patient</th>
                                                <th>Chief Complaint</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.map(r => (
                                                <tr key={r.id}>
                                                    <td>
                                                        <div className="fw-semibold">{r.record_identifier}</div>
                                                        <div style={{ fontSize: '.72rem', color: '#878a99' }}>{r.name}</div>
                                                    </td>
                                                    <td>
                                                        <div>{r.patient_name}</div>
                                                        <div style={{ fontSize: '.72rem', color: '#878a99' }}>{r.patient_id}</div>
                                                    </td>
                                                    <td className="text-truncate" style={{ maxWidth: 180 }}>{r.chief_complaint || '—'}</td>
                                                    <td>
                                                        <span className={`uz-badge ${statusBadge(r.status)}`}>{r.status}</span>
                                                        {r.requires_follow_up && <span className="uz-badge uz-badge-pending ms-1" style={{ fontSize: '.65rem' }}>Follow-up</span>}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.date_created)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-capsule text-warning" /> Recent Prescriptions</h6>
                        </div>
                        <div className="uz-panel-body">
                            {prescriptions.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-capsule fs-1 d-block mb-2 opacity-50" />
                                    No prescriptions issued.
                                </div>
                            ) : (
                                prescriptions.map(rx => (
                                    <div key={rx.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 36, height: 36, fontSize: '.9rem', background: 'rgba(247,184,75,.10)', color: '#f7b84b' }}>
                                            <i className="bx bx-capsule" />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.84rem' }}>{rx.medication}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{rx.patient_name} • {rx.dosage}</div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-transfer-alt text-danger" /> Active Referrals</h6>
                            <Link to="/institution/clinician/referrals" className="btn btn-sm btn-outline-danger rounded-pill">Manage Referrals</Link>
                        </div>
                        <div className="uz-panel-body">
                            {referrals.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-transfer-alt fs-1 d-block mb-2 opacity-50" />
                                    No active referrals.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '.82rem' }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Referral</th>
                                                <th>Patient</th>
                                                <th>Facility</th>
                                                <th>Type</th>
                                                <th>Priority</th>
                                                <th>Direction</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {referrals.map(ref => (
                                                <tr key={`${ref.id}-${ref.direction}`}>
                                                    <td className="fw-semibold">{ref.referral_id}</td>
                                                    <td>{ref.patient_name}</td>
                                                    <td>{ref.receiving_facility}</td>
                                                    <td className="text-capitalize">{ref.referral_type?.replace('_', ' ')}</td>
                                                    <td>
                                                        <span className={`uz-badge ${ref.priority === 'emergency' ? 'uz-badge-closed' : ref.priority === 'urgent' ? 'uz-badge-pending' : 'uz-badge-active'}`}>
                                                            {ref.priority}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`uz-badge ${ref.direction === 'inbound' ? 'uz-badge-active' : 'uz-badge-completed'}`}>
                                                            <i className={`bx ${ref.direction === 'inbound' ? 'bx-log-in-circle' : 'bx-log-out-circle'} me-1`} />
                                                            {ref.direction}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-group text-success" /> Care Team</h6>
                        </div>
                        <div className="uz-panel-body">
                            {roster.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-group fs-1 d-block mb-2 opacity-50" />
                                    No team members loaded.
                                </div>
                            ) : (
                                roster.map(s => (
                                    <div key={s.id} className="uz-staff-row">
                                        {s.avatar && !s.avatar.includes('avatar.png') ? (
                                            <img src={s.avatar} className="uz-staff-avatar" alt="" />
                                        ) : (
                                            <div className="uz-staff-avatar-placeholder" style={{ background: randomColor(s.email), color: '#fff' }}>
                                                {getInitials(s.fname, s.lname)}
                                            </div>
                                        )}
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="uz-staff-name text-truncate">{s.fname} {s.lname}</div>
                                            <div className="uz-staff-title">{s.title || s.email}</div>
                                        </div>
                                        <span className={`uz-badge ${s.active ? 'uz-badge-active' : 'uz-badge-closed'}`}>{s.active ? 'Active' : 'Inactive'}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ─── Facility Panels ──────────────────────────────────────────────── */
const FacilityPanels: React.FC<{ data: ProviderDashboardData | null }> = ({ data }) => {
    const facilities = data?.facility_network || [];
    const staffBreakdown = data?.staff_by_title || [];
    const serviceLines = data?.service_lines || [];
    const billing = data?.billing_overview;
    const flow = data?.patient_flow;
    const collaborations = data?.recent_collaborations || [];
    const totalStaff = staffBreakdown.reduce((a, b) => a + b.count, 0);

    return (
        <>
            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-buildings text-primary" /> Facility Network</h6>
                            <Link to="/institution/facility/facilities" className="btn btn-sm btn-outline-primary rounded-pill">Manage Facilities</Link>
                        </div>
                        <div className="uz-panel-body">
                            {facilities.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-buildings fs-1 d-block mb-2 opacity-50" />
                                    No facilities in network.
                                </div>
                            ) : (
                                facilities.map(f => (
                                    <div key={f.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 40, height: 40, fontSize: '1rem', background: 'rgba(10,179,156,.10)', color: '#0ab39c' }}>
                                            <i className="bx bx-buildings" />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.86rem' }}>{f.name}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{f.category || 'General'} &bull; {f.town || 'Unset'}</div>
                                        </div>
                                        <div className="text-end me-2" style={{ minWidth: 130 }}>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{f.patient_count} patients &bull; {f.staff_count} staff</div>
                                            <div style={{ fontSize: '.72rem', color: '#099885' }}>{f.utilization}% utilization</div>
                                        </div>
                                        <span className={`uz-badge ${f.active ? 'uz-badge-active' : 'uz-badge-closed'}`}>{f.active ? 'Active' : 'Inactive'}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-group text-success" /> Workforce by Role</h6>
                            <Link to="/institution/facility/staff" className="btn btn-sm btn-outline-success rounded-pill">View Staff</Link>
                        </div>
                        <div className="uz-panel-body">
                            {staffBreakdown.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-group fs-1 d-block mb-2 opacity-50" />
                                    No staff data available.
                                </div>
                            ) : (
                                <>
                                    {staffBreakdown.map(item => {
                                        const pct = totalStaff > 0 ? Math.round((item.count / totalStaff) * 100) : 0;
                                        return (
                                            <div key={item.title} className="mb-3">
                                                <div className="d-flex justify-content-between" style={{ fontSize: '.82rem' }}>
                                                    <span className="fw-semibold">{item.title}</span>
                                                    <span className="text-muted">{item.count} ({pct}%)</span>
                                                </div>
                                                <div className="progress" style={{ height: 6 }}>
                                                    <div className="progress-bar" role="progressbar" style={{ width: `${pct}%`, background: randomColor(item.title) }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="text-end text-muted" style={{ fontSize: '.76rem' }}>Total active staff: <strong>{totalStaff}</strong></div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {flow && (
                <div className="row g-3 mb-4">
                    {[
                        { label: 'Total Patients', value: flow.total_patients, icon: 'bx bx-user', color: '#405189', bg: 'rgba(64,81,137,.12)' },
                        { label: 'Enrolled (30d)', value: flow.enrolled_last_30d, icon: 'bx bx-user-plus', color: '#0ab39c', bg: 'rgba(10,179,156,.12)' },
                        { label: 'Enrolled (7d)', value: flow.enrolled_last_7d, icon: 'bx bx-user-check', color: '#299cdb', bg: 'rgba(41,156,219,.12)' },
                        { label: 'Appointments Today', value: flow.appointments_today, icon: 'bx bx-calendar-check', color: '#f7b84b', bg: 'rgba(247,184,75,.12)' },
                        { label: 'Appts This Week', value: flow.appointments_this_week, icon: 'bx bx-calendar-event', color: '#f06548', bg: 'rgba(240,101,72,.12)' },
                    ].map(item => (
                        <div key={item.label} className="col">
                            <div className="uz-stat-card">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="uz-stat-icon" style={{ background: item.bg, color: item.color }}>
                                        <i className={item.icon} />
                                    </div>
                                    <div>
                                        <div className="uz-stat-value">{item.value.toLocaleString()}</div>
                                        <div className="uz-stat-label">{item.label}</div>
                                        <div className="uz-stat-sub">Patient flow</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="row g-4 mb-4">
                <div className="col-lg-7">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-briefcase-alt text-primary" /> Service Lines</h6>
                            <Link to="/institution/facility/services" className="btn btn-sm btn-outline-primary rounded-pill">Manage Services</Link>
                        </div>
                        <div className="uz-panel-body">
                            {serviceLines.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-briefcase-alt fs-1 d-block mb-2 opacity-50" />
                                    No service lines configured.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '.82rem' }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Service</th>
                                                <th>Facility</th>
                                                <th className="text-end">Min Rate</th>
                                                <th className="text-end">Hourly</th>
                                                <th>Billable</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceLines.map(svc => (
                                                <tr key={svc.id}>
                                                    <td className="fw-semibold">{svc.name}</td>
                                                    <td>{svc.clinic_name}</td>
                                                    <td className="text-end">{Number(svc.minimum_rate).toLocaleString()}</td>
                                                    <td className="text-end">{Number(svc.hourly_rate).toLocaleString()}</td>
                                                    <td>
                                                        <span className={`uz-badge ${svc.is_billable ? 'uz-badge-active' : 'uz-badge-closed'}`}>
                                                            {svc.is_billable ? 'Yes' : 'No'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-receipt text-warning" /> Billing Summary</h6>
                            <Link to="/institution/facility/accounting" className="btn btn-sm btn-outline-warning rounded-pill">Full Accounting</Link>
                        </div>
                        <div className="uz-panel-body">
                            {!billing ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-receipt fs-1 d-block mb-2 opacity-50" />
                                    No billing data available.
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {[
                                        { label: 'Clinic Billing', value: billing.clinic_billing_total, count: billing.clinic_billing_count, color: '#405189' },
                                        { label: 'Institution Billing', value: billing.institution_billing_total, count: billing.institution_billing_count, color: '#0ab39c' },
                                        { label: 'Clinic Invoices', value: billing.clinic_invoice_total, count: billing.clinic_invoice_count, color: '#299cdb' },
                                        { label: 'Institution Invoices', value: billing.institution_invoice_total, count: billing.institution_invoice_count, color: '#f7b84b' },
                                    ].map(b => (
                                        <div key={b.label} className="col-6">
                                            <div className="p-3 rounded-3" style={{ background: '#f8f9fa', border: '1px solid #eef2f7' }}>
                                                <div style={{ fontSize: '.72rem', color: '#878a99' }}>{b.label}</div>
                                                <div className="fw-bold" style={{ fontSize: '1.1rem', color: b.color }}>{Number(b.value).toLocaleString()}</div>
                                                <div style={{ fontSize: '.68rem', color: '#abb4c4' }}>{b.count} records</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-12">
                    <div className="uz-panel">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-link-alt text-primary" /> Recent Collaborations</h6>
                            <Link to="/institution/collaborations" className="btn btn-sm btn-outline-primary rounded-pill">View All</Link>
                        </div>
                        <div className="uz-panel-body">
                            {collaborations.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-link-alt fs-1 d-block mb-2 opacity-50" />
                                    No collaborations yet.
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {collaborations.map(c => (
                                        <div key={c.id} className="col-md-6 col-lg-4">
                                            <div className="uz-collab-row" style={{ border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 16px' }}>
                                                <div className="uz-quick-icon flex-shrink-0" style={{ width: 36, height: 36, fontSize: '.9rem', background: 'rgba(64,81,137,.08)', color: '#405189' }}>
                                                    <i className="bx bx-link-alt" />
                                                </div>
                                                <div className="flex-grow-1 min-width-0">
                                                    <div className="fw-semibold text-truncate" style={{ fontSize: '.84rem' }}>{c.title}</div>
                                                    <div style={{ fontSize: '.72rem', color: '#878a99' }}>Created {formatDate(c.created)}</div>
                                                </div>
                                                <span className={`uz-badge ${statusBadge(c.status)}`}>{c.status}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ─── General Service Provider Panels ──────────────────────────────── */
const GeneralPanels: React.FC<{ data: ProviderDashboardData | null }> = ({ data }) => {
    const facilities = data?.facility_network || [];
    const roster = data?.staff_roster || [];
    const collaborations = data?.recent_collaborations || [];
    const logins = data?.recent_logins || [];
    const patients = data?.recent_patients || [];

    return (
        <>
            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-buildings text-primary" /> Network and Operations</h6>
                            <Link to="/institution/clinics" className="btn btn-sm btn-outline-primary rounded-pill">View Facilities</Link>
                        </div>
                        <div className="uz-panel-body">
                            {facilities.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-buildings fs-1 d-block mb-2 opacity-50" />
                                    No facilities available.
                                </div>
                            ) : (
                                facilities.map(f => (
                                    <div key={f.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 40, height: 40, fontSize: '1rem', background: 'rgba(10,179,156,.10)', color: '#0ab39c' }}>
                                            <i className="bx bx-buildings" />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.86rem' }}>{f.name}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{f.category || 'General facility'} &bull; {f.town || 'Location not set'}</div>
                                        </div>
                                        <div className="text-end me-2" style={{ minWidth: 120 }}>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>{f.patient_count} patients &bull; {f.staff_count} staff</div>
                                            <div style={{ fontSize: '.72rem', color: '#099885' }}>{f.utilization}% utilization</div>
                                        </div>
                                        <span className={`uz-badge ${f.active ? 'uz-badge-active' : 'uz-badge-closed'}`}>{f.active ? 'Active' : 'Inactive'}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-group text-success" /> Active Team</h6>
                            <Link to="/institution/professionals" className="btn btn-sm btn-outline-success rounded-pill">View All</Link>
                        </div>
                        <div className="uz-panel-body">
                            {roster.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-group fs-1 d-block mb-2 opacity-50" />
                                    No staff loaded.
                                </div>
                            ) : (
                                roster.map(s => (
                                    <div key={s.id} className="uz-staff-row">
                                        {s.avatar && !s.avatar.includes('avatar.png') ? (
                                            <img src={s.avatar} className="uz-staff-avatar" alt="" />
                                        ) : (
                                            <div className="uz-staff-avatar-placeholder" style={{ background: randomColor(s.email), color: '#fff' }}>
                                                {getInitials(s.fname, s.lname)}
                                            </div>
                                        )}
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="uz-staff-name text-truncate">{s.fname} {s.lname}</div>
                                            <div className="uz-staff-title">{s.title || s.email}</div>
                                        </div>
                                        <span className={`uz-badge ${s.active ? 'uz-badge-active' : 'uz-badge-closed'}`}>{s.active ? 'Active' : 'Inactive'}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-link-alt text-primary" /> Recent Collaborations</h6>
                            <Link to="/institution/collaborations" className="btn btn-sm btn-outline-primary rounded-pill">View All</Link>
                        </div>
                        <div className="uz-panel-body">
                            {collaborations.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-link-alt fs-1 d-block mb-2 opacity-50" />
                                    No collaborations yet.
                                </div>
                            ) : (
                                collaborations.map(c => (
                                    <div key={c.id} className="uz-collab-row">
                                        <div className="uz-quick-icon flex-shrink-0" style={{ width: 36, height: 36, fontSize: '.9rem', background: 'rgba(64,81,137,.08)', color: '#405189' }}>
                                            <i className="bx bx-link-alt" />
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="fw-semibold text-truncate" style={{ fontSize: '.84rem' }}>{c.title}</div>
                                            <div style={{ fontSize: '.72rem', color: '#878a99' }}>Created {formatDate(c.created)}</div>
                                        </div>
                                        <span className={`uz-badge ${statusBadge(c.status)}`}>{c.status}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-time-five text-warning" /> Recent Activity</h6>
                        </div>
                        <div className="uz-panel-body">
                            {logins.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-time-five fs-1 d-block mb-2 opacity-50" />
                                    No recent login activity.
                                </div>
                            ) : (
                                logins.map((l, i) => (
                                    <div key={i} className="uz-activity-item">
                                        <div className="uz-activity-dot" style={{ background: l.success ? '#0ab39c' : '#f06548' }} />
                                        <div className="flex-grow-1">
                                            <div className="uz-activity-text">
                                                <span className="fw-semibold text-capitalize">{l.user_type}</span> login
                                                {l.user_name ? ` — ${l.user_name}` : ''}
                                            </div>
                                            <div className="uz-activity-time">{timeAgo(l.created)}</div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-12">
                    <div className="uz-panel h-100">
                        <div className="uz-panel-header">
                            <h6 className="uz-section-title mb-0"><i className="bx bx-user-plus text-info" /> Recently Active Patients</h6>
                            <Link to="/institution/patients" className="btn btn-sm btn-outline-info rounded-pill">Open Patient Center</Link>
                        </div>
                        <div className="uz-panel-body">
                            {patients.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bx bx-user-circle fs-1 d-block mb-2 opacity-50" />
                                    No recent patients found.
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {patients.map(p => (
                                        <div key={p.patient_id} className="col-md-6 col-xl-4">
                                            <div className="uz-quick-card text-start">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="uz-staff-avatar-placeholder" style={{ background: randomColor(p.patient_id), color: '#fff' }}>
                                                        {getInitials(p.first_name, p.last_name)}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1">{p.first_name} {p.last_name}</h6>
                                                        <small className="d-block">Patient ID: {p.patient_id}</small>
                                                        <small className="d-block">Enrolled {formatDate(p.enrolled)}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export const GeneralServiceProviderDashboard: React.FC = () => <ProviderDashboard dashboardKey="general-service-provider" />;
export const ClinicianDashboard: React.FC = () => <ProviderDashboard dashboardKey="clinician" />;
export const FacilityDashboard: React.FC = () => <ProviderDashboard dashboardKey="facility" />;

export default ProviderDashboard;
